import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/news";
import { formatViews, relativeTime } from "@/utils/news";

type Props = {
  item: NewsItem;
  index?: number;
  variant?: "row" | "compact";
};

export default function NewsCard({ item, index, variant = "row" }: Props) {
  if (variant === "compact") {
    return (
      <Link
        to={`/detail/${item.id}`}
        className="group flex items-start gap-3 py-3.5 border-b border-background-200/70 last:border-b-0 cursor-pointer"
      >
        {typeof index === "number" && (
          <span
            className={`mt-0.5 w-5 shrink-0 font-label text-lg leading-none font-bold ${
              index < 3 ? "text-primary-500" : "text-foreground-300"
            }`}
          >
            {`${index + 1}`.padStart(2, "0")}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-6 text-foreground-900 line-clamp-2-safe group-hover:text-primary-600 transition-colors">
            {item.title}
          </span>
          <span className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
            <span className="text-secondary-600">{item.category}</span>
            <span className="h-3 w-px bg-background-300" />
            <span>{relativeTime(item.publishedAt)}</span>
          </span>
        </span>
      </Link>
    );
  }

  return (
    <article>
      <Link
        to={`/detail/${item.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-3.5 md:p-4 rounded-lg border border-background-200/80 bg-background-50 hover:border-primary-300 hover:bg-primary-50/30 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-[224px] h-[180px] sm:h-[138px] shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={item.image}
            alt={item.title}
            title={`${item.title} - 爆料网图片`}
            className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
          />
        </div>
        <div className="min-w-0 flex-1 flex flex-col">
          <div className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center h-[22px] px-2 rounded bg-primary-100 text-primary-700 font-medium">
              {item.category}
            </span>
            <span className="text-foreground-400">{relativeTime(item.publishedAt)}</span>
            <span className="hidden sm:inline-flex items-center gap-1 text-foreground-400">
              <i className="ri-eye-line" />
              {formatViews(item.views)}
            </span>
          </div>
          <h3 className="mt-2 text-[16px] md:text-[17px] font-bold leading-[1.55] text-foreground-950 group-hover:text-primary-600 transition-colors">
            {item.title}
          </h3>
          <p className="mt-1.5 text-[13px] leading-6 text-foreground-600 line-clamp-2-safe">
            {item.summary}
          </p>
          <div className="mt-auto pt-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 min-w-0">
              {item.tags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 rounded-full bg-background-100 text-[11px] text-foreground-600 whitespace-nowrap"
                >
                  # {tag}
                </span>
              ))}
            </div>
            <span className="hidden sm:flex items-center gap-1 text-xs text-foreground-400 shrink-0">
              查看详情
              <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform" />
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}